import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { v4 as uuid } from 'uuid';
import type { LearnosityRequest } from '../models/LearnosityRequest';
import type { ItemsAppInterface } from '../vite-env';
import { acquireLearnosityItemsSdk, type LearnosityRecoveryAction } from '../utils/learnosityItemsSdk';
import { LearnosityRetryMismatchError, LearnositySdkTimeoutError } from '../utils/learnosityErrors';
import { saveLearnosityResponses } from '../utils/learnositySave';
import {
  addLearnosityBreadcrumb,
  addLearnosityErrorBreadcrumb,
  classifyLearnosityFailure,
  createLearnosityTelemetry,
  type LearnosityFailureCategory,
  type LearnosityErrorShape,
  type LearnosityTelemetry,
} from '../utils/learnositySentry';

export type LearnosityInitializationStatus = 'idle' | 'initializing' | 'retry_wait' | 'ready' | 'failed';
/** Runtime math recovery for a ready Challenge; failed saves preserve the player. */
export type LearnosityMathRecovery = 'idle' | 'saving' | 'action_required' | 'save_failed';
type MathReloadBlock = 'reload_already_used' | 'reload_storage_unavailable';
const STARTUP_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000] as const;
const MATH_RELOAD_MARKER = 'learnosity:10023:auto-reload-used';

/** Each tab gets one automatic math reload; unreadable storage disables it. */
function automaticMathReloadBlock(): MathReloadBlock | undefined {
  try {
    return window.sessionStorage.getItem(MATH_RELOAD_MARKER) === null ? undefined : 'reload_already_used';
  } catch {
    return 'reload_storage_unavailable';
  }
}

function claimAutomaticMathReload(): MathReloadBlock | undefined {
  const block = automaticMathReloadBlock();
  if (block) return block;
  try {
    window.sessionStorage.setItem(MATH_RELOAD_MARKER, '1');
    return undefined;
  } catch {
    return 'reload_storage_unavailable';
  }
}

type AssessmentType =
  | 'skill'
  | 'inline_assessment'
  | 'practice_problems'
  | 'diagnostic'
  | 'diagnostic_demo'
  | 'exit_slip'
  | 'exit_slip_print'
  | 'assessment_review'
  | 'individual_report';
export type LearnosityInitializationRefetch = (
  initialization: LearnosityRequest,
  signal: AbortSignal,
) => Promise<LearnosityRequest>;

interface Options {
  initObj: LearnosityRequest | null;
  enabled?: boolean;
  restartKey?: number | string;
  reloadOn10001AfterReady?: boolean;
  assessmentType: AssessmentType;
  getContainer: () => HTMLElement | null;
  onReady: (instance: ItemsAppInterface, telemetry: LearnosityTelemetry) => void;
  onFailure?: (error: unknown) => void;
  /** Ready Challenge players: resolve true only once responses are persisted for a math-recovery reload. */
  saveBeforeReload?: (itemsApp: ItemsAppInterface) => Promise<boolean>;
  refetchInitialization: LearnosityInitializationRefetch;
}

function requestSettings({ meta: _meta, ...settings }: LearnosityRequest['request']) {
  // Signing metadata may change. All assessment settings must remain identical.
  return JSON.stringify(settings, (_key, value) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
      : value,
  );
}

interface Attempt {
  instance: ItemsAppInterface | null;
  telemetry: LearnosityTelemetry;
  failed: boolean;
  ready: boolean;
  runtimeRecoveryHandled: boolean;
  sdk: ReturnType<typeof acquireLearnosityItemsSdk>;
  error?: unknown;
}

interface Cycle {
  disposed: boolean;
  terminal: boolean;
  controller: AbortController;
  attempt: Attempt | null;
  timer?: ReturnType<typeof setTimeout>;
  deadline?: ReturnType<typeof setTimeout>;
}

export function useLearnosityItemsInitialization({
  initObj,
  enabled = true,
  restartKey,
  reloadOn10001AfterReady = false,
  assessmentType,
  getContainer,
  onReady,
  onFailure,
  saveBeforeReload = saveLearnosityResponses,
  refetchInitialization,
}: Options) {
  const posthog = usePostHog();
  const latest = useRef({ getContainer, onReady, onFailure, saveBeforeReload, posthog });
  const inputs = [initObj, enabled, assessmentType, restartKey, reloadOn10001AfterReady] as const;
  const committedCycle = useRef({ inputs, active: false, resume: () => {} });
  useLayoutEffect(() => {
    if (committedCycle.current.inputs.some((value, index) => !Object.is(value, inputs[index]))) {
      committedCycle.current = { inputs, active: false, resume: () => {} };
    }
    const owner = committedCycle.current;
    latest.current = { getContainer, onReady, onFailure, saveBeforeReload, posthog };
    owner.active = true;
    owner.resume();
    // Suspense can disconnect layout effects without disposing the running player.
    // Hold delivery until this same cycle reconnects, or passive cleanup cancels it.
    return () => {
      owner.active = false;
    };
  });
  const [status, setStatus] = useState<LearnosityInitializationStatus>('idle');
  const [mathRecovery, setMathRecovery] = useState<LearnosityMathRecovery>('idle');
  const itemsAppRef = useRef<ItemsAppInterface | null>(null);
  const recoverMathRef = useRef<(() => void) | null>(null);
  const retryMathRecovery = useCallback(() => recoverMathRef.current?.(), []);

  useEffect(() => {
    setMathRecovery('idle');
    if (!initObj || !enabled) {
      setStatus('idle');
      return;
    }

    const cycle: Cycle = { disposed: false, terminal: false, attempt: null, controller: new AbortController() };
    const startupCycleId = uuid();
    const owner = committedCycle.current;
    let phase = 'sdk_wait';
    const refetch = refetchInitialization;
    const originalSettings = requestSettings(initObj.request);
    const startedAt = performance.now();
    const deadlineAt = startedAt + 120_000;
    let triggerCode: string | undefined;
    let retriesScheduled = 0;
    let recoveryAction: LearnosityRecoveryAction | undefined;
    let outcomeRecorded = false;
    const isCurrent = () => !cycle.disposed && !cycle.terminal && owner === committedCycle.current;
    const isActive = (attempt: Attempt) => isCurrent() && cycle.attempt === attempt && !attempt.failed;
    const elapsedMs = () => Math.max(0, Math.round(performance.now() - startedAt));
    const pending: Array<() => void> = [];
    const deliver = (work: () => void) => {
      if (!isCurrent()) return;
      if (owner.active) work();
      else pending.push(work);
    };
    owner.resume = () => pending.splice(0).forEach(deliver);

    // The trigger code stays the first SDK failure; an exhausted startup also records its final cause.
    const recordOutcome = (outcome: 'recovered' | 'exhausted', failureCategory?: LearnosityFailureCategory) => {
      if (!triggerCode || outcomeRecorded || !isCurrent()) return;
      outcomeRecorded = true;
      latest.current.posthog.capture(`learnosity_init_${outcome}`, {
        assessment_type: assessmentType,
        error_code: triggerCode,
        elapsed_ms: elapsedMs(),
        retries_scheduled: retriesScheduled,
        ...(recoveryAction && { recovery_action: recoveryAction }),
        ...(failureCategory && { failure_category: failureCategory }),
      });
    };

    const resetAttempt = (attempt: Attempt) => {
      const instance = attempt.instance;
      if (!instance) return;
      attempt.instance = null;
      attempt.telemetry.markReset();
      try {
        instance.reset();
      } catch {
        // TEAMF-1147: reset can throw for a failed, incomplete SDK instance.
        // Catch it without preventing the existing automatic recovery path.
        addLearnosityBreadcrumb('reset_error', { api: 'items', assessmentType, detail: 'reset_failure' });
      }
    };

    const finishFailure = (attempt: Attempt, error: unknown, operation?: string) => {
      if (!isCurrent()) return;
      attempt.failed = true;
      attempt.sdk.release(() => resetAttempt(attempt));
      itemsAppRef.current = null;
      attempt.telemetry.captureError(error, operation, { terminalStartup: true });
      recordOutcome('exhausted', classifyLearnosityFailure(error, operation));
      cycle.terminal = true;
      clearTimeout(cycle.timer);
      clearTimeout(cycle.deadline);
      cycle.controller.abort();
      setStatus('failed');
      latest.current.onFailure?.(error);
    };

    const reportMathRecovery = (
      outcome: 'reloading' | 'save_failed' | 'action_required',
      automatic: boolean,
      reason?: MathReloadBlock,
    ) =>
      latest.current.posthog.capture('learnosity_runtime_math_recovery', {
        assessment_type: assessmentType,
        outcome,
        automatic,
        ...(reason && { reason }),
      });

    // Wait for a confirmed save before reloading; keep the ready player on failure.
    const offerMathRecovery = (attempt: Attempt, recovery: 'action_required' | 'save_failed') => {
      recoverMathRef.current = () => deliver(() => void reloadAfterSave(attempt, false));
      setMathRecovery(recovery);
    };

    const blockAutomaticMathReload = (attempt: Attempt, reason: MathReloadBlock) => {
      addLearnosityBreadcrumb('runtime_reload_blocked', {
        api: 'items',
        assessmentType,
        code: '10023',
        detail: reason,
      });
      reportMathRecovery('action_required', true, reason);
      offerMathRecovery(attempt, 'action_required');
    };

    const reloadAfterSave = async (attempt: Attempt, automatic: boolean) => {
      const instance = attempt.instance;
      if (!instance || !isActive(attempt)) return;
      recoverMathRef.current = null;
      setMathRecovery('saving');
      // Formula editors queue model updates for the next task. Let those updates
      // and the input lock settle before Learnosity snapshots responses to save.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      if (!isActive(attempt)) return;
      let saved = false;
      try {
        saved = await latest.current.saveBeforeReload(instance);
      } catch {
        // An unconfirmed save is a failed save; the responses remain in the ready player.
      }
      deliver(() => {
        if (!isActive(attempt)) return;
        if (!saved) {
          reportMathRecovery('save_failed', automatic);
          offerMathRecovery(attempt, 'save_failed');
          return;
        }
        // Claim the tab's automatic reload only once the save is confirmed.
        const blocked = automatic ? claimAutomaticMathReload() : undefined;
        if (blocked) {
          blockAutomaticMathReload(attempt, blocked);
          return;
        }
        addLearnosityBreadcrumb('runtime_reload', { api: 'items', assessmentType, code: '10023' });
        reportMathRecovery('reloading', automatic);
        window.location.reload();
      });
    };

    const recoverMathRendering = (attempt: Attempt) => {
      const blocked = automaticMathReloadBlock();
      if (blocked) blockAutomaticMathReload(attempt, blocked);
      else void reloadAfterSave(attempt, true);
    };

    const initialize = (initialization: LearnosityRequest) => {
      if (!isCurrent()) return;
      setStatus('initializing');
      const telemetry = createLearnosityTelemetry({
        startupCycleId,
        assessmentType,
        activityReference: initialization.request.activity_id,
        renderingType: initialization.request.rendering_type,
        assessmentState: initialization.request.state,
        learnerId: initialization.request.user_id,
        sessionId: initialization.request.session_id,
        getContainer: () => latest.current.getContainer(),
      });
      const attempt: Attempt = {
        instance: null,
        telemetry,
        failed: false,
        ready: false,
        runtimeRecoveryHandled: false,
        sdk: acquireLearnosityItemsSdk(deadlineAt),
      };
      cycle.attempt = attempt;
      telemetry.markInit();

      const ready = () => {
        attempt.sdk.markReady();
        deliver(() => {
          if (!isActive(attempt) || attempt.ready || !attempt.instance) return;
          attempt.ready = true;
          clearTimeout(cycle.deadline);
          itemsAppRef.current = attempt.instance;
          telemetry.markReady();
          setStatus('ready');
          recordOutcome('recovered');
          latest.current.onReady(attempt.instance, telemetry);
        });
      };
      const fail = (error: unknown, sdkCallback = false) => {
        attempt.sdk.markFailed(error);
        deliver(() => {
          if (!isActive(attempt)) return;
          // Preserve numeric 10001 reloads unless Challenge math recovery already
          // owns the save and reload decision for this player.
          if (
            sdkCallback &&
            attempt.ready &&
            reloadOn10001AfterReady &&
            !(assessmentType === 'skill' && attempt.runtimeRecoveryHandled) &&
            (error as LearnosityErrorShape | null)?.code === 10001
          ) {
            attempt.failed = true;
            clearTimeout(cycle.timer);
            cycle.controller.abort();
            telemetry.captureError(error);
            window.location.reload();
            return;
          }
          if (attempt.ready) {
            telemetry.captureError(error);
            // Experimental practice re-signs after any native SDK error, as before.
            // Challenge and standard practice retain their math-only handling.
            const recoverRuntime =
              assessmentType === 'practice_problems'
                ? sdkCallback
                : String((error as LearnosityErrorShape | null)?.code) === '10023' &&
                  (assessmentType === 'skill' || assessmentType === 'inline_assessment');
            if (recoverRuntime && !attempt.runtimeRecoveryHandled && isActive(attempt)) {
              attempt.runtimeRecoveryHandled = true;
              if (assessmentType !== 'skill') {
                latest.current.onFailure?.(error);
                return;
              }
              recoverMathRendering(attempt);
            }
            return;
          }
          if (error instanceof LearnositySdkTimeoutError) {
            triggerCode ??= 'unknown';
            finishFailure(attempt, error, 'startup_timeout');
            return;
          }
          attempt.failed = true;
          attempt.error = error;
          // Retain the first failure for telemetry independently of the retry budget.
          if (!triggerCode) {
            const rawCode = (error as LearnosityErrorShape | null)?.code;
            const code = typeof rawCode === 'string' || typeof rawCode === 'number' ? String(rawCode).trim() : '';
            triggerCode = /^\d{5}$/.test(code) ? code : 'unknown';
            addLearnosityErrorBreadcrumb(error, {
              api: 'items',
              assessmentType,
              renderPhase: 'before_ready',
              operation: 'init',
            });
          }
          const retryDelay = STARTUP_RETRY_DELAYS_MS[retriesScheduled];
          if (retryDelay !== undefined) {
            retriesScheduled += 1;
            addLearnosityBreadcrumb('init_retry', {
              api: 'items',
              assessmentType,
              code: triggerCode,
              elapsedMs: elapsedMs(),
            });
            resetAttempt(attempt);
            phase = 'retry_wait';
            setStatus('retry_wait');
            cycle.timer = setTimeout(() => deliver(() => void retryInitialization()), retryDelay);
          } else {
            finishFailure(attempt, error);
          }
        });
      };

      // Keep callbacks safe even if init invokes them before returning its instance.
      const start = () => {
        if (!isActive(attempt)) return;
        // Suspense may defer delivery after availability resolves. Recheck before
        // invoking the SDK so a resumed player joins any newer replacement.
        let wait: Promise<void> | null;
        try {
          wait = attempt.sdk.waitUntilAvailable(cycle.controller.signal);
        } catch (error) {
          fail(error);
          return;
        }
        if (wait) {
          void wait.then(
            () => deliver(start),
            error => {
              if (isActive(attempt)) fail(error);
            },
          );
          return;
        }
        phase = 'initializing';
        let initializing = true;
        let synchronousFailure: { error: unknown } | undefined;
        const pendingCallbacks: Array<() => void> = [];
        try {
          attempt.instance = attempt.sdk.init(initialization, {
            readyListener: () => (initializing ? pendingCallbacks.push(ready) : ready()),
            errorListener: error => (initializing ? pendingCallbacks.push(() => fail(error, true)) : fail(error, true)),
          });
        } catch (error) {
          synchronousFailure = { error };
        }
        initializing = false;
        pendingCallbacks.forEach(callback => callback());
        // Preserve a callback emitted before a throw as the first startup failure.
        // Application onReady exceptions above remain outside this catch.
        if (synchronousFailure && isActive(attempt)) fail(synchronousFailure.error);
      };
      start();
    };

    const finishRetryFailure = (attempt: Attempt, error: unknown) => {
      if (cycle.controller.signal.aborted) return;
      deliver(() =>
        finishFailure(
          attempt,
          error,
          error instanceof LearnositySdkTimeoutError ? 'startup_timeout' : 'retry_init_object',
        ),
      );
    };

    const recoverInitialization = async (attempt: Attempt, initialization: LearnosityRequest) => {
      try {
        // Refresh signing data, while preserving the assessment and saved session.
        if (!initialization?.request || requestSettings(initialization.request) !== originalSettings) {
          throw new LearnosityRetryMismatchError();
        }
        phase = 'sdk_reload';
        const recovery = attempt.sdk.recover(attempt.error, cycle.controller.signal);
        // The lease chooses its action before its first wait.
        const action = attempt.sdk.recoveryAction;
        recoveryAction ??= action;
        if (action === 'xdomain_transport') phase = 'sdk_wait';
        await recovery;
      } catch (error) {
        finishRetryFailure(attempt, error);
        return;
      }
      // Application ready handlers must stay outside recovery's error boundary.
      deliver(() => {
        attempt.sdk.release();
        phase = 'sdk_wait';
        initialize(initialization);
      });
    };

    const retryInitialization = async () => {
      const attempt = cycle.attempt;
      if (!isCurrent() || !attempt) return;
      try {
        phase = 'signing_refresh';
        const initialization = await refetch(initObj, cycle.controller.signal);
        deliver(() => void recoverInitialization(attempt, initialization));
      } catch (error) {
        finishRetryFailure(attempt, error);
      }
    };

    // One deadline starts with SDK initialization, including signing refresh and
    // loader recovery. Initial signing/item reads precede this budget.
    cycle.deadline = setTimeout(
      () =>
        deliver(() => {
          const attempt = cycle.attempt;
          if (!isCurrent() || !attempt || attempt.ready) return;
          triggerCode ??= 'unknown';
          finishFailure(
            attempt,
            new Error(`Learnosity startup timed out after 120 seconds during ${phase}`),
            'startup_timeout',
          );
        }),
      Math.max(0, deadlineAt - performance.now()),
    );
    initialize(initObj);
    return () => {
      cycle.disposed = true;
      pending.length = 0;
      owner.resume = () => {};
      itemsAppRef.current = null;
      recoverMathRef.current = null;
      clearTimeout(cycle.timer);
      clearTimeout(cycle.deadline);
      cycle.controller.abort();
      if (cycle.attempt) {
        const attempt = cycle.attempt;
        attempt.sdk.release(() => resetAttempt(attempt));
      }
    };
    // Signing refresh belongs to this cycle; changing a callback must not restart it.
  }, [initObj, enabled, assessmentType, restartKey, reloadOn10001AfterReady]);

  return { status, itemsAppRef, mathRecovery, retryMathRecovery };
}
